import React from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import { kebabCase, upperFirst } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const tooltipDisplayPercent = (percent) => +(percent * 100).toFixed(1);

export default function StatusBar({
  onTrack = 0,
  minorDisruption = 0,
  majorDisruption = 0,
  upcoming = 0,
  discontinued = 0,
  completed = 0,
  statusPending = 0,
  settings: {
    onTrack: labelOnTrack = 'On Track',
    completed: labelCompleted = 'Completed',
    someDisruption: labelMinorDisruption = 'Minor Disruption',
    majorDisruption: labelMajorDisruption = 'Major Disruption',
    upcoming: labelUpcoming = 'Upcoming',
    statusPending: labelStatusPending = 'Status Pending',
    discontinued: labelDiscontinued = 'Discontinued',
  },
  keyPrefix,
  isCell = false,
}) {
  if (!onTrack && !minorDisruption && !majorDisruption && !upcoming && !discontinued && !completed && !statusPending) {
    return null;
  }

  let { green, purple, red, yellow, grey, blue, brown } = [];

  if (onTrack) {
    green = (
      <span
        className="status on-track"
        style={{ width: `${onTrack * 100}%` }}
        data-for={`statusTip-${keyPrefix}`}
        data-tip={`${labelOnTrack} ${tooltipDisplayPercent(onTrack)}%`}
        role="status"
        aria-label={`${labelOnTrack} ${tooltipDisplayPercent(onTrack)}%`}
      />
    );
  }

  if (minorDisruption) {
    yellow = (
      <span
        className="status minor-disruption"
        style={{ width: `${minorDisruption * 100}%` }}
        data-for={`statusTip-${keyPrefix}`}
        data-tip={`${labelMinorDisruption} ${tooltipDisplayPercent(minorDisruption)}%`}
        role="status"
        aria-label={`${labelMinorDisruption} ${tooltipDisplayPercent(minorDisruption)}%`}
      />
    );
  }

  if (majorDisruption) {
    red = (
      <span
        className="status major-disruption"
        style={{ width: `${majorDisruption * 100}%` }}
        data-for={`statusTip-${keyPrefix}`}
        data-tip={`${labelMajorDisruption} ${tooltipDisplayPercent(majorDisruption)}%`}
        role="status"
        aria-label={`${labelMajorDisruption} ${tooltipDisplayPercent(majorDisruption)}%`}
      />
    );
  }

  if (upcoming) {
    brown = (
      <span
        className="status upcoming"
        style={{ width: `${upcoming * 100}%` }}
        data-for={`statusTip-${keyPrefix}`}
        data-tip={`${labelUpcoming} ${tooltipDisplayPercent(upcoming)}%`}
        role="status"
        aria-label={`${labelUpcoming} ${tooltipDisplayPercent(upcoming)}%`}
      />
    );
  }

  if (discontinued) {
    purple = (
      <span
        className="status discontinued"
        style={{ width: `${discontinued * 100}%` }}
        data-for={`statusTip-${keyPrefix}`}
        data-tip={`${labelDiscontinued} ${tooltipDisplayPercent(discontinued)}%`}
        role="status"
        aria-label={`${labelDiscontinued} ${tooltipDisplayPercent(discontinued)}%`}
      />
    );
  }

  if (completed) {
    blue = (
      <span
        className="status completed"
        style={{ width: `${completed * 100}%` }}
        data-for={`statusTip-${keyPrefix}`}
        data-tip={`${labelCompleted} ${tooltipDisplayPercent(completed)}%`}
        role="status"
        aria-label={`${labelCompleted} ${tooltipDisplayPercent(completed)}%`}
      />
    );
  }

  if (statusPending) {
    grey = (
      <span
        className="status status-pending"
        style={{ width: `${statusPending * 100}%` }}
        data-for={`statusTip-${keyPrefix}`}
        data-tip={`${labelStatusPending} ${tooltipDisplayPercent(statusPending)}%`}
        role="status"
        aria-label={`${labelCompleted} ${tooltipDisplayPercent(statusPending)}%`}
      />
    );
  }

  let completedMessage;

  if (completed === 1) {
    completedMessage = (
      <span className="completed-thumb" role="status" aria-label={`Completed`}>
        <FontAwesomeIcon icon={['fal', 'thumbs-up']} />
        <span>Completed</span>
      </span>
    );
  } else {
    [
      {
        onTrack,
      },
      {
        minorDisruption,
      },
      {
        majorDisruption,
      },
      {
        upcoming,
      },
      {
        discontinued,
      },
      {
        statusPending,
      },
    ].forEach((status) => {
      const stateValue = Object.values(status)[0];
      const stateLabel = Object.keys(status)[0];
      if (stateValue === 1) {
        completedMessage = (
          <span className="action-lv-label">
            <span className={kebabCase(stateLabel).toLowerCase()}>
              {
                {
                  labelOnTrack,
                  labelStatusPending,
                  labelMajorDisruption,
                  labelUpcoming,
                  labelDiscontinued,
                  labelMinorDisruption,
                }[`label${upperFirst(stateLabel)}`]
              }
            </span>
          </span>
        );
      }
    });
  }

  return (
    <div className={`status-bar${completed === 1 ? ' completed' : ''}`} {...(isCell ? { role: 'cell' } : {})}>
      {completedMessage}
      <div className="child-bar">
        {grey}
        {green}
        {yellow}
        {red}
        {brown}
        {purple}
        {blue}
      </div>
      {!completedMessage ? <ReactTooltip id={`statusTip-${keyPrefix}`} /> : null}
    </div>
  );
}

StatusBar.propTypes = {
  onTrack: PropTypes.number,
  minorDisruption: PropTypes.number,
  majorDisruption: PropTypes.number,
  upcoming: PropTypes.number,
  discontinued: PropTypes.number,
  completed: PropTypes.number,
  statusPending: PropTypes.number,
  settings: PropTypes.shape({
    onTrack: PropTypes.string,
    completed: PropTypes.string,
    someDisruption: PropTypes.string,
    majorDisruption: PropTypes.string,
    upcoming: PropTypes.string,
    statusPending: PropTypes.string,
    discontinued: PropTypes.string,
  }).isRequired,
  keyPrefix: PropTypes.string.isRequired,
  isCell: PropTypes.bool,
};
